import React, { useState, useEffect } from 'react';
import { IonIcon, IonToast } from '@ionic/react';
import confetti from 'canvas-confetti';
import {
closeOutline,
informationCircleOutline
} from 'ionicons/icons';
import GoogleMapsAutocomplete from '../../../components/GoogleMapsAutocomplete';
import ForwardIcon from "../../../icons/Forwardicon";
import BackIcon from "../../../icons/Backicon";
import ShieldCheckIcon from '../../../icons/Shieldcheck';
import CheckCircleIcon from '../../../icons/Circlecheck';
import UploadIcon from "../../../icons/Uploadicon";
import { useIonRouter } from '@ionic/react';
import { YummyText } from '../../../components/YummyText';
import { removeVerificationNotification } from '../../../utils/verificationNotifications';
import { submitRiderVerification, getRiderProfile } from '../../../utils/authApi';
import { getCookie, setCookie, deleteCookie, setJSONCookie, getJSONCookie } from '../../../utils/cookies';
import StyledDropdown from '../../../components/StyledDropdown';

const VerificationPromptModal = ({ isOpen, onClose, previousFeedback }) => {
  const router = useIonRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resubmitAttempted, setResubmitAttempted] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [showToast, setShowToast] = useState(false);


  useEffect(() => {
    console.log('[VerificationPromptModal] 👁️ isOpen prop changed:', isOpen);
  }, [isOpen]);

  const [formData, setFormData] = useState({
    // Contact Information
    phoneNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    // Coordinates from autocomplete
    lat: 0,
    lng: 0,
    // Identity
    idType: '',
    idNumber: '',
    idDocument: null,
    profilePhoto: null,
    // Vehicle
    vehicleType: '',
    makeModel: '',
    year: '',
    licensePlate: '',
    driversLicense: null,
    insurance: null,
    // Agreement
    agreeBackgroundCheck: false
  });

  // Trigger confetti when success modal shows
  useEffect(() => {
    if (showSuccessModal) {
      const duration = 3000;
      const end = Date.now() + duration;
      const colors = ['#00B876', '#00D68F', '#DCFCE7', '#FFD700', '#FF6B9D'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());

      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: colors
        });
      }, 200);
    }
  }, [showSuccessModal]);

  const steps = [
    { id: 1, name: 'Contact', active: true },
    { id: 2, name: 'Identity', active: false },
    { id: 3, name: 'Vehicle', active: false },
    { id: 4, name: 'Review', active: false }
  ];

  // if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Compress image files
  const compressImage = async (file, maxSizeMB = 1) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions (max 1920x1920)
          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Start with quality 0.8 and reduce if needed
          let quality = 0.8;
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`[VerificationModal] Compressed ${file.name}:`, {
                  originalSize: (file.size / 1024 / 1024).toFixed(2) + 'MB',
                  compressedSize: (compressedFile.size / 1024 / 1024).toFixed(2) + 'MB',
                  reduction: ((1 - compressedFile.size / file.size) * 100).toFixed(1) + '%'
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleFileUpload = async (field, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid JPG, JPEG, PNG, or PDF file');
      return;
    }

    let processedFile = file;

    // Compress images before storing
    if (file.type.startsWith('image/')) {
      try {
        // Show processing message
        console.log(`[VerificationModal] Processing ${field}...`);
        processedFile = await compressImage(file, 1); // Max 1MB per image

        // Final size check after compression
        if (processedFile.size > 2 * 1024 * 1024) {
          alert('File is still too large after compression. Please use a smaller image.');
          return;
        }
      } catch (error) {
        console.error('[VerificationModal] Compression error:', error);
        alert('Failed to process image. Please try another file.');
        return;
      }
    } else if (file.type === 'application/pdf') {
      // For PDFs, enforce stricter size limit
      if (file.size > 3 * 1024 * 1024) {
        alert('PDF file must be less than 3MB');
        return;
      }
    }

    console.log(`[VerificationModal] File ready for ${field}:`, {
      name: processedFile.name,
      size: (processedFile.size / 1024).toFixed(2) + 'KB',
      type: processedFile.type
    });

    handleInputChange(field, processedFile);
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Add this to the handleSubmit function in VerificationPromptModal
  // Replace the existing token check section

  const handleSubmit = async () => {
    if (!formData.agreeBackgroundCheck) {
      alert('Please accept the background check authorization');
      return;
    }

    // CRITICAL AUTH CHECK
    const riderToken = getCookie('rider_token');
    const authToken = getCookie('auth_token');
    const customerToken = getCookie('customer_token');

    console.log('[VerificationModal] 🔍 Auth Check:', {
      riderToken: riderToken ? '✓ Present' : '✗ Missing',
      authToken: authToken ? '✓ Present' : '✗ Missing',
      customerToken: customerToken ? '✓ Present' : '✗ Missing',
      allCookies: document.cookie
    });

    const hasToken = !!(riderToken || authToken || customerToken);

    if (!hasToken) {
      console.error('[VerificationModal] ❌ NO AUTHENTICATION TOKEN FOUND!');
      alert('Authentication required. You need to verify your email first, then log in before submitting documents.');

      // Redirect to login
      window.location.href = '/rider/login';
      return;
    }

    console.log('[VerificationModal] ✓ Token verified, proceeding with submission');

    // Validate files
    if (!formData.idDocument || !(formData.idDocument instanceof File)) {
      alert('Please upload your ID document');
      return;
    }
    if (!formData.profilePhoto || !(formData.profilePhoto instanceof File)) {
      alert('Please upload your profile photo');
      return;
    }
    if (!formData.driversLicense || !(formData.driversLicense instanceof File)) {
      alert("Please upload your driver's license");
      return;
    }

    // Check total upload size
    const totalSize = (formData.idDocument?.size || 0) +
      (formData.profilePhoto?.size || 0) +
      (formData.driversLicense?.size || 0) +
      (formData.insurance?.size || 0);

    const totalSizeMB = totalSize / 1024 / 1024;

    console.log('[VerificationModal] 📦 Total upload size:', {
      idDocument: (formData.idDocument.size / 1024).toFixed(2) + 'KB',
      profilePhoto: (formData.profilePhoto.size / 1024).toFixed(2) + 'KB',
      driversLicense: (formData.driversLicense.size / 1024).toFixed(2) + 'KB',
      insurance: formData.insurance ? (formData.insurance.size / 1024).toFixed(2) + 'KB' : 'Not provided',
      total: totalSizeMB.toFixed(2) + 'MB'
    });

    // Enforce maximum total size of 8MB
    if (totalSize > 8 * 1024 * 1024) {
      alert(`Total file size (${totalSizeMB.toFixed(2)}MB) exceeds 8MB limit. Please use smaller images.`);
      return;
    }

    setUploading(true);

    let submitData = null;
    try {
      submitData = new FormData();

      // Add all form fields
      submitData.append('contactInfo[phone]', formData.phoneNumber || '');
      submitData.append('contactInfo[streetAddress]', formData.streetAddress || '');
      submitData.append('contactInfo[city]', formData.city || '');
      submitData.append('contactInfo[state]', formData.state || '');
      submitData.append('contactInfo[zipCode]', formData.zipCode || '');

      submitData.append('identity[idType]', formData.idType || '');
      submitData.append('identity[idNumber]', formData.idNumber || '');

      submitData.append('vehicle[type]', formData.vehicleType || '');
      submitData.append('vehicle[makeModel]', formData.makeModel || '');
      submitData.append('vehicle[year]', formData.year ? parseInt(formData.year) : '');
      submitData.append('vehicle[licensePlate]', formData.licensePlate || '');

      submitData.append('backgroundCheckConsent', true);

      // Add files with explicit filenames
      submitData.append('idDocument', formData.idDocument, formData.idDocument.name);
      submitData.append('profilePhoto', formData.profilePhoto, formData.profilePhoto.name);
      submitData.append('driversLicense', formData.driversLicense, formData.driversLicense.name);

      if (formData.insurance && formData.insurance instanceof File) {
        submitData.append('insurance', formData.insurance, formData.insurance.name);
      }

      console.log('[VerificationModal] 📤 Submitting verification documents to API...');
      console.log('[VerificationModal] ℹ️ This may take a moment due to file uploads...');

      const response = await submitRiderVerification(submitData);

      console.log('[VerificationModal] ✅ Submission successful:', response);

      // Store verification data in cookies for profile sync
      const verificationData = {
        contactInfo: {
          phone: formData.phoneNumber,
          streetAddress: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode
        },
        vehicle: {
          type: formData.vehicleType,
          makeModel: formData.makeModel,
          year: formData.year,
          licensePlate: formData.licensePlate
        },
        identity: {
          idType: formData.idType,
          idNumber: formData.idNumber
        }
      };

      // Store in cookies for profile access
      setJSONCookie('riderVerificationData', verificationData, 7);
      console.log('[VerificationModal] 💾 Verification data stored in cookies');

      // Mark verification as submitted - CRITICAL: Use consistent values
      setCookie('verificationCompleted', 'true', 7);
      setCookie('verificationSubmitted', 'true', 7);
      setCookie('riderAccountVerified', 'pending', 7);
      setCookie('riderVerificationStatus', 'pending', 7);

      console.log('[VerificationModal] ✓ Verification flags set in cookies');

      // Clean up
      deleteCookie('verificationPromptDismissedAt');
      deleteCookie('nextVerificationPushNotification');
      deleteCookie('lastVerificationPushNotification');

      // Refresh profile and update cookies
      try {
        console.log('[VerificationModal] 🔄 Fetching updated profile...');
        const profileResponse = await getRiderProfile();
        const profile = profileResponse?.data?.driver || profileResponse?.driver || profileResponse?.data;

        if (profile) {
          // Update user_data cookie with latest profile info
          const existingUserData = getJSONCookie('user_data') || {};
          const updatedUserData = {
            ...existingUserData,
            ...profile,
            phone: formData.phoneNumber || existingUserData.phone,
            verificationStatus: 'pending'
          };
          setJSONCookie('user_data', updatedUserData, 7);
          console.log('[VerificationModal] ✓ user_data cookie updated with profile');
        }

        // Dispatch event for other components to refresh
        window.dispatchEvent(new CustomEvent('verification:completed', {
          detail: {
            profile: profile,
            verificationData: verificationData
          }
        }));
        console.log('[VerificationModal] 📢 verification:completed event dispatched');
      } catch (profileError) {
        console.error('[VerificationModal] Profile refresh failed:', profileError);
        // Still dispatch event even if profile fetch fails
        window.dispatchEvent(new CustomEvent('verification:completed', {
          detail: { verificationData: verificationData }
        }));
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error('[VerificationModal] ❌ Submission failed:', error);

      const serverMsg = (
        (error?.response && (error.response.data?.message || error.response.data?.error || error.response.data)) ||
        error?.data?.message ||
        error?.message ||
        ''
      ).toString().toLowerCase();

      // If server claims application already submitted, and we haven't retried yet,
      // clear the local submitted flag and retry once.
      if (!resubmitAttempted && (serverMsg.includes('already') && serverMsg.includes('submit') || serverMsg.includes('verification application already submitted'))) {
        try {
          setCookie('verificationSubmitted', '', -1);
        } catch (e) { /* ignore */ }
        setResubmitAttempted(true);
        try {
          setToastMsg('Retrying submission...');
          setShowToast(true);
          const retryResp = await submitRiderVerification(submitData);
          const verificationData = {
            contactInfo: {
              phone: formData.phoneNumber,
              streetAddress: formData.streetAddress,
              city: formData.city,
              state: formData.state,
              zipCode: formData.zipCode
            },
            vehicle: {
              type: formData.vehicleType,
              makeModel: formData.makeModel,
              year: formData.year,
              licensePlate: formData.licensePlate
            },
            identity: {
              idType: formData.idType,
              idNumber: formData.idNumber
            }
          };

          setJSONCookie('riderVerificationData', verificationData, 7);
          setCookie('verificationCompleted', 'true', 7);
          setCookie('verificationSubmitted', 'true', 7);
          setCookie('riderAccountVerified', 'pending', 7);
          setCookie('riderVerificationStatus', 'pending', 7);

          try {
            const profileResponse = await getRiderProfile();
            const profile = profileResponse?.data?.driver || profileResponse?.driver || profileResponse?.data;
            if (profile) {
              const existingUserData = getJSONCookie('user_data') || {};
              setJSONCookie('user_data', { ...existingUserData, ...profile, verificationStatus: 'pending' }, 7);
            }
          } catch (e) { /* ignore */ }

          setShowSuccessModal(true);
          return;
        } catch (retryErr) {
          console.error('[VerificationModal] Retry failed:', retryErr);
          alert(retryErr.message || 'Failed to submit documents on retry. Please contact support.');
        }
      }

      if (error.status === 401 || serverMsg.includes('token') || serverMsg.includes('auth')) {
        alert('Session expired. Please log in again and retry.');
        window.location.href = '/rider/login';
      } else {
        alert(error.message || 'Failed to submit documents. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDismiss = () => {
    setCookie('verificationPromptDismissedAt', Date.now().toString(), 1);
    setCurrentStep(1);
    setFormData({
      phoneNumber: '',
      streetAddress: '',
      lat: 0,
      lng: 0,
      idType: '',
      idNumber: '',
      idDocument: null,
      profilePhoto: null,
      vehicleType: '',
      makeModel: '',
      year: '',
      licensePlate: '',
      driversLicense: null,
      insurance: null,
      agreeBackgroundCheck: false
    });
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleDismiss();
    }
  };

  const getStepColor = (stepId) => {
    if (stepId < currentStep) return 'text-[#00D68F]';
    if (stepId === currentStep) return 'text-[#00D68F]';
    return 'text-gray-400';
  };

  const getProgressWidth = () => {
    return `${((currentStep - 1) / 3) * 100}%`;
  };

  const isStep1Valid = () => {
    // Allow progressing after phone and street are provided even if coords are not set.
    // Coordinates are nice-to-have for better matching but not required to continue.
    const phone = (formData.phoneNumber || '').toString().trim();
    const street = (formData.streetAddress || '').toString().trim();
    return phone !== '' && street !== '';
  };

  const isStep2Valid = () => {
    const idNumber = (formData.idNumber || '').toString().trim();
    return (formData.idType || '') !== '' &&
      idNumber !== '' &&
      formData.idDocument !== null &&
      formData.profilePhoto !== null;
  };

  const isStep3Valid = () => {
    const makeModel = (formData.makeModel || '').toString().trim();
    const year = (formData.year || '').toString().trim();
    const license = (formData.licensePlate || '').toString().trim();
    return (formData.vehicleType || '') !== '' &&
      makeModel !== '' &&
      year !== '' &&
      license !== '' &&
      formData.driversLicense !== null;
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1: return isStep1Valid();
      case 2: return isStep2Valid();
      case 3: return isStep3Valid();
      case 4: return formData.agreeBackgroundCheck;
      default: return false;
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      onClick={handleBackdropClick}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        style={{ backdropFilter: 'blur(8px)' }}
      />

      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-20"
        >
          <IonIcon icon={closeOutline} className="text-gray-600 text-xl" />
        </button>

        <div className="overflow-y-auto max-h-[90vh]">
          <div className="p-8 pb-6">
            <div className="text-center mb-6">
              <YummyText>
                <h1 className="text-3xl font-bold text-[#00B75A] mb-2">Swiftly</h1>

                <IonToast
                  isOpen={showToast}
                  message={toastMsg}
                  duration={3000}
                  onDidDismiss={() => setShowToast(false)}
                />
                <h2 className="text-3xl font-sm text-[#0A0A0A] mb-0">Verify Your Account</h2>
                <p className="text-[#717182] text-xs font-[400]">Complete your rider profile to start delivering</p>
              </YummyText>
            </div>

            <div className="flex items-center justify-center gap-1 mb-4">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <YummyText>
                    <span className={`text-xs font-[300] ${getStepColor(step.id)}`}>
                      {step.name}
                    </span>
                  </YummyText>
                  {index < steps.length - 1 && (
                    <span className="text-gray-300 mx-1">›</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="relative h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full bg-[#00D68F] transition-all duration-300"
                style={{ width: getProgressWidth() }}
              />
            </div>

            {previousFeedback && String(previousFeedback).trim() && (
              <div className="mt-4 mx-6 mb-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-medium text-amber-800 mb-1">Previous feedback</p>
                <p className="text-sm text-[#0F172A]">{previousFeedback}</p>
                <p className="text-xs text-[#64748B] mt-1">Use the feedback above to improve your application.</p>
              </div>
            )}
          </div>

          <div className="px-6 pb-8">
            {currentStep === 1 && (
              <div className="space-y-6">
                <YummyText>
                  <div>
                    <h3 className="text-medium font-[400] text-[#0F172A] mb-1">Contact Information</h3>
                    <p className="text-xs text-[#4A5565] mb-4">We need your contact details to reach you and verify your location.</p>
                  </div>

                  <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-2 flex gap-2">
                    <IonIcon icon={informationCircleOutline} className="text-[#1E40AF] text-lg flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#1E40AF]">All information is encrypted and securely stored.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-5">Phone Number *</label>
                    <input
                      type="tel"
                      placeholder="+234 908 767 4240"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      className="w-full px-4 py-3 bg-[#F3F3F5] placeholder:text-[#717182] border-none rounded-lg focus:ring-2 focus:ring-[#00D68F] outline-none"
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2 mt-3">
                      <label className="block text-sm font-medium text-[#0F172A]">Address *</label>
                      <div className="group relative">
                        <IonIcon icon={informationCircleOutline} className="text-[#64748B] w-4 h-4 mt-2.5" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50">
                          Please enter a precise location for accurate delivery service.
                        </div>
                      </div>
                    </div>
                    <GoogleMapsAutocomplete
                      value={formData.streetAddress}
                      onChange={(value) => handleInputChange('streetAddress', value)}
                      placeholder="Enter your address"
                      onPlaceSelect={(place) => {
                        handleInputChange('streetAddress', place.street || place.formatted_address || place);
                        handleInputChange('lat', place.coordinates?.lat || 0);
                        handleInputChange('lng', place.coordinates?.lng || 0);
                        handleInputChange('city', place.city || '');
                        handleInputChange('state', place.state || '');
                        handleInputChange('zipCode', place.zipCode || '');
                      }}
                    />
                  </div>
                </YummyText>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <YummyText>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0F172A] mb-1">Identity Verification</h3>
                    <p className="text-sm text-[#64748B] mb-4">Upload your government-issued ID and a recent photo for verification.</p>
                  </div>

                  <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-3 flex gap-2">
                    <IonIcon icon={informationCircleOutline} className="text-[#3B82F6] text-xl flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#1E40AF]">Accepted formats: JPG, PNG, PDF. Max file size: 10MB.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-5">ID Type *</label>
                    <StyledDropdown
                      value={formData.idType || 'Select ID type'}
                      onChange={(val) => handleInputChange('idType', val === 'Select ID type' ? '' : val)}
                      options={['Select ID type', 'Driver\'s License', 'International Passport', 'National ID Card', 'Voter\'s Card']}
                      className="w-full"
                      width="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">ID Number *</label>
                    <input
                      type="text"
                      placeholder="Input ID number"
                      value={formData.idNumber}
                      onChange={(e) => handleInputChange('idNumber', e.target.value)}
                      className="w-full px-4 py-3 bg-[#F3F3F5] placeholder:text-[#717182] border-none rounded-lg focus:ring-2 focus:ring-[#00D68F] outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">Upload ID Document *</label>
                      <label className="flex items-center gap-2 px-4 py-3 bg-[#F3F3F5] border-none rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <span className="text-sm text-[#717182] flex-1 truncate">
                          {formData.idDocument ? formData.idDocument.name : 'Select a file'}
                        </span>
                        <UploadIcon size={16} stroke="#717182" />
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload('idDocument', e)}
                        />
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">Profile Photo *</label>
                      <label className="flex items-center gap-2 px-4 py-3 bg-[#F3F3F5] border-none rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <span className="text-sm text-[#717182] flex-1 truncate">
                          {formData.profilePhoto ? formData.profilePhoto.name : 'Select a file'}
                        </span>
                        <UploadIcon size={16} stroke="#717182" />
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          className="hidden"
                          onChange={(e) => handleFileUpload('profilePhoto', e)}
                        />
                      </label>
                    </div>
                  </div>
                </YummyText>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <YummyText>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0F172A] mb-1">Vehicle Information</h3>
                    <p className="text-sm text-[#64748B] mb-4">Tell us about your vehicle and provide your driver's license.</p>
                  </div>

                  <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-3 flex gap-2">
                    <IonIcon icon={informationCircleOutline} className="text-[#3B82F6] text-xl flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#1E40AF]">Your vehicle must be in good working condition and meet local requirements.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-5">Vehicle Type *</label>
                    <StyledDropdown
                      value={formData.vehicleType || 'Select vehicle type'}
                      onChange={(val) => handleInputChange('vehicleType', val === 'Select vehicle type' ? '' : val)}
                      options={['Select vehicle type', 'Motorcycle', 'Scooter', 'Bicycle', 'Car', 'Van']}
                      className="w-full"
                      width="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">Make/Model *</label>
                      <input
                        type="text"
                        placeholder="Honda Civic"
                        value={formData.makeModel}
                        onChange={(e) => handleInputChange('makeModel', e.target.value)}
                        className="w-full px-4 py-3 bg-[#F8F9FA] placeholder:text-[#717182] border-none rounded-lg focus:ring-2 focus:ring-[#00D68F] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">Year *</label>
                      <input
                        type="text"
                        placeholder="2020"
                        value={formData.year}
                        onChange={(e) => handleInputChange('year', e.target.value)}
                        className="w-full px-4 py-3 bg-[#F8F9FA] border-none rounded-lg focus:ring-2 focus:ring-[#00D68F] outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">License Plate *</label>
                    <input
                      type="text"
                      placeholder="ABC-1234"
                      value={formData.licensePlate}
                      onChange={(e) => handleInputChange('licensePlate', e.target.value)}
                      className="w-full px-4 py-3 bg-[#F8F9FA] placeholder:text-[#717182] border-none rounded-lg focus:ring-2 focus:ring-[#00D68F] outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">Driver's License *</label>
                      <label className="flex items-center gap-2 px-4 py-3 bg-[#F8F9FA] border-none rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <span className="text-sm text-[#717182] flex-1 truncate">
                          {formData.driversLicense ? formData.driversLicense.name : "Select a file"}
                        </span>
                        <UploadIcon size={16} stroke="#717182" />
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload('driversLicense', e)}
                        />
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2 mt-3">Insurance (Optional)</label>
                      <label className="flex items-center gap-2 px-4 py-3 bg-[#F8F9FA] border-none rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <span className="text-sm text-[#717182] flex-1 truncate">
                          {formData.insurance ? formData.insurance.name : 'Select a file'}
                        </span>
                        <UploadIcon size={16} stroke="#717182" />
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload('insurance', e)}
                        />
                      </label>
                    </div>
                  </div>
                </YummyText>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <YummyText>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0F172A] mb-1">Review & Submit</h3>
                    <p className="text-sm text-[#64748B] mb-4">Please review your information and accept the background check authorization.</p>
                  </div>

                  <div className="bg-[#F8F9FA] rounded-xl p-4 divide-y divide-gray-300">
                    <div className="pb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-[#0F172A]">Contact</h4>
                        <button onClick={() => setCurrentStep(1)} className="text-sm text-[#00D68F] hover:text-[#00B876] font-medium">Edit</button>
                      </div>
                      <p className="text-sm text-[#64748B]">{formData.phoneNumber || 'Not provided'}</p>
                      <p className="text-sm text-[#64748B]">{formData.streetAddress || 'Not provided'}, {formData.city || 'Not provided'}, {formData.state || 'Not provided'}</p>
                    </div>

                    <div className="py-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-[#0F172A]">Identity</h4>
                        <button onClick={() => setCurrentStep(2)} className="text-sm text-[#00D68F] hover:text-[#00B876] font-medium">Edit</button>
                      </div>
                      <p className="text-sm text-[#64748B]">{formData.idType || 'Not provided'}</p>
                      <p className="text-sm text-[#64748B]">ID: {formData.idNumber || 'Not provided'}</p>
                    </div>

                    <div className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-[#0F172A]">Vehicle</h4>
                        <button onClick={() => setCurrentStep(3)} className="text-sm text-[#00D68F] hover:text-[#00B876] font-medium">Edit</button>
                      </div>
                      <p className="text-sm text-[#64748B]">{formData.vehicleType || 'Not provided'} - {formData.makeModel || 'Not provided'} ({formData.year || 'Not provided'})</p>
                      <p className="text-sm text-[#64748B]">Plate: {formData.licensePlate || 'Not provided'}</p>
                    </div>
                  </div>

                  <div className="bg-[#EDFFEE] border border-[#A7FFB3] rounded-lg p-3 mb-2 mt-4 flex gap-2">
                    <ShieldCheckIcon width={20} height={20} color="#059F00" />
                    <p className="text-xs text-[#059F00]">By submitting this application, you authorize Swiftly Express to conduct background checks and verify your information.</p>
                  </div>

                  <label className="flex items-start gap-2 p-3 rounded-lg bg-[#EFF6FF] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.agreeBackgroundCheck}
                      onChange={(e) => handleInputChange('agreeBackgroundCheck', e.target.checked)}
                      className="mt-1 w-3 h-3 text-[#00D68F] border border-[#BEDBFF] rounded focus:ring-[#00D68F]"
                    />
                    <span className="text-xs text-[#64748B]">
                      I authorize Swiftly Express to conduct a background check and verify my driving record. I understand this is required for platform safety and I certify that all information provided is accurate.
                    </span>
                  </label>
                </YummyText>
              </div>
            )}

            <YummyText>
              <div className="flex gap-3 mt-8">
                {currentStep > 1 ? (
                  <button
                    onClick={prevStep}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
                    style={{ border: "1px solid #0000001A" }}
                  >
                    <BackIcon size={18} color="#0A0A0A" />
                    <span className="font-medium text-[#0A0A0A]">Back</span>
                  </button>
                ) : (
                  <button
                    onClick={handleDismiss}
                    className="flex-1 px-6 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-[#0A0A0A]"
                    style={{ border: "1px solid #0000001A" }}
                  >
                    Cancel
                  </button>
                )}

                {currentStep < 4 ? (
                  <button
                    onClick={nextStep}
                    disabled={!isCurrentStepValid()}
                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#00D68F] text-white rounded-xl transition-colors font-medium ${isCurrentStepValid() ? 'hover:bg-[#00B876] opacity-100' : 'opacity-50 cursor-not-allowed'
                      }`}
                  >
                    <span>Continue</span>
                    <ForwardIcon size={18} color="#FFFFFF" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={!formData.agreeBackgroundCheck || uploading}
                    className={`flex-1 px-6 py-3 rounded-xl transition-colors font-medium flex items-center justify-center gap-2 ${formData.agreeBackgroundCheck && !uploading
                      ? 'bg-[#00D68F] hover:bg-[#00B876] text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    {uploading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    )}
                    {uploading ? 'Uploading Documents...' : 'Submit for Verification'}
                  </button>
                )}
              </div>
            </YummyText>
          </div>
        </div>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center animate-scale-in">
            <YummyText>
              <div className="w-16 h-16 bg-[#DCFCE7] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon width={32} height={32} color="#00B876" />
              </div>
              <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-3">Application Submitted!</h2>
              <div className="bg-[#EFF6FF] rounded-xl p-4 mb-4">
                <p className="text-sm flex text-justify leading-relaxed text-[#64748B] mb-3">
                  <IonIcon icon={informationCircleOutline} className="text-[#3B82F6] text-lg flex-shrink-0 mt-0.5 mr-2" />
                  Your application is under review. Our team will verify your documents and conduct necessary background checks.
                </p>
                <p className="text-sm font-[700] text-[#1447E6]">
                  <img src="/clockicon.svg" alt="Clock Icon" className="inline-block w-4 h-4 mr-3" />
                  <span className="text-sm font-[300] text-[#1447E6]">Expected review time:</span> 2-5 business days
                </p>
              </div>
              <p className="text-xs text-[#4A5565] mb-6">
                We'll notify you via email once your account has been approved. You can then start accepting deliveries and earning!
              </p>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  onClose();
                  // Force a small delay to ensure cookies are written before reload
                  setTimeout(() => {
                    window.location.reload();
                  }, 100);
                }}
                className="w-full py-3 bg-[#00B75A] hover:bg-[#00B876] text-white text-sm rounded-xl transition-colors font-medium"
              >
                Go to Dashboard
              </button>
              <p className="text-xs text-[#6A7282] mt-3">You'll receive an email confirmation shortly</p>
            </YummyText>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default VerificationPromptModal;